// PostPosterPage.js — Anonymous poster upload page
// Mobile-first design. No account required.

import React, { useCallback, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import {
  getClientIp,
  uploadPoster,
  validateFile,
} from '../services/posterService';

// ─── Sub-components ───────────────────────────────────────────

const LimitReachedModal = ({ onClose }) => (
  <div className='fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-60 px-6'>
    <div className='bg-white rounded-2xl p-8 max-w-sm w-full text-center shadow-2xl'>
      <div className='text-4xl mb-4'>🙂</div>
      <p className='text-gray-900 font-semibold text-lg mb-2'>
        Today's limit is reached.
      </p>
      <p className='text-gray-500 text-sm mb-6'>Come back tomorrow.</p>
      <button
        onClick={onClose}
        className='w-full bg-black text-white py-3 rounded-xl font-medium text-sm hover:bg-gray-800 transition-colors'
      >
        OK
      </button>
    </div>
  </div>
);

const SuccessView = ({ onViewCollabs, onSignUp, isPermanent }) => (
  <div className='flex flex-col items-center justify-center min-h-screen bg-white px-6 text-center'>
    <div className='mb-6'>
      <div className='w-16 h-16 rounded-full bg-black flex items-center justify-center mx-auto mb-4'>
        <svg className='w-8 h-8 text-white' fill='none' viewBox='0 0 24 24' stroke='currentColor'>
          <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2.5} d='M5 13l4 4L19 7' />
        </svg>
      </div>
      {isPermanent ? (
        <h2 className='text-2xl font-semibold text-gray-900 leading-snug'>
          Your poster is live.<br />
          <span className='font-bold'>It stays forever.</span>
        </h2>
      ) : (
        <h2 className='text-2xl font-semibold text-gray-900 leading-snug'>
          Your poster is live for<br />the next{' '}
          <span className='font-bold'>Three Weeks.</span>
        </h2>
      )}
    </div>

    <div className='w-full max-w-xs flex flex-col gap-3 mt-4'>
      <button
        onClick={onViewCollabs}
        className='w-full bg-black text-white py-4 rounded-xl font-medium text-sm border border-transparent hover:bg-gradient-to-r hover:from-blue-500 hover:to-purple-500 hover:border-transparent transition-all duration-200'
      >
        View in Collaborations
      </button>
      {!isPermanent && (
        <button
          onClick={onSignUp}
          className='w-full border border-black text-black py-4 rounded-xl font-medium text-sm hover:bg-gray-50 transition-colors'
        >
          Sign up to keep it forever
        </button>
      )}
    </div>
  </div>
);

// ─── Main Page ─────────────────────────────────────────────────

const PostPosterPage = () => {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);

  const [stage, setStage] = useState('idle'); // idle | preview | uploading | success | error
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [errorMsg, setErrorMsg] = useState('');
  const [showLimitModal, setShowLimitModal] = useState(false);
  const [isPermanent, setIsPermanent] = useState(false);

  // ── File selection ──────────────────────────────────────────
  const handleFileChange = useCallback(e => {
    const file = e.target.files?.[0];
    if (!file) return;

    const { valid, error } = validateFile(file);
    if (!valid) {
      setErrorMsg(error);
      setStage('error');
      return;
    }

    const url = URL.createObjectURL(file);
    setSelectedFile(file);
    setPreviewUrl(url);
    setErrorMsg('');
    setStage('preview');
  }, []);

  const handleChooseFile = () => {
    setStage('idle');
    setErrorMsg('');
    fileInputRef.current?.click();
  };

  // ── Upload ───────────────────────────────────────────────────
  const handleUpload = useCallback(async () => {
    if (!selectedFile) return;

    setStage('uploading');
    setUploadProgress(0);

    // Simulate progress animation while upload runs
    const progressInterval = setInterval(() => {
      setUploadProgress(prev => (prev < 85 ? prev + 5 : prev));
    }, 200);

    try {
      const ip = await getClientIp();
      const result = await uploadPoster(selectedFile, ip);

      clearInterval(progressInterval);
      setUploadProgress(100);

      if (result.success) {
        setIsPermanent(result.isPermanent || false);
        setTimeout(() => setStage('success'), 400);
      } else if (result.error === 'LIMIT_REACHED') {
        setStage('idle');
        setShowLimitModal(true);
      } else {
        setErrorMsg(result.error || 'Upload failed. Please try again.');
        setStage('error');
      }
    } catch (err) {
      clearInterval(progressInterval);
      setErrorMsg('Something went wrong. Please try again.');
      setStage('error');
    }
  }, [selectedFile]);

  // ── Navigation ────────────────────────────────────────────────
  const handleViewCollabs = () => navigate('/tagme?tab=collaborations');
  const handleSignUp = () => navigate('/register');

  // ── Cleanup on re-select ─────────────────────────────────────
  const handleReset = () => {
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setSelectedFile(null);
    setPreviewUrl(null);
    setStage('idle');
    setErrorMsg('');
    setUploadProgress(0);
  };

  // ── Render: success ──────────────────────────────────────────
  if (stage === 'success') {
    return (
      <SuccessView
        onViewCollabs={handleViewCollabs}
        onSignUp={handleSignUp}
        isPermanent={isPermanent}
      />
    );
  }

  // ── Render: main ─────────────────────────────────────────────
  return (
    <div className='min-h-screen bg-white flex flex-col'>

      {showLimitModal && (
        <LimitReachedModal onClose={() => setShowLimitModal(false)} />
      )}

      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type='file'
        accept='image/jpeg,image/png,image/webp,image/gif'
        className='hidden'
        onChange={handleFileChange}
      />

      {/* Content */}
      <div className='flex-1 flex flex-col justify-start px-7 pt-4 pb-10 max-w-sm mx-auto w-full'>

        {/* Logo — centered */}
        <div className='mb-6 flex justify-center'>
          <img
            src={`${process.env.PUBLIC_URL}/TAG_Logo_01.png`}
            alt='TAG Logo'
            className='h-9 w-auto'
          />
        </div>

        {/* Copy — editorial */}
        <div className='mb-10 space-y-5'>
          <p className='text-gray-700 text-[15px] font-light leading-loose tracking-wide'>
            I'm graduating soon. Before I leave, I wanted to fix one small thing:
          </p>
          <p className='text-gray-700 text-[15px] font-light leading-loose tracking-wide'>
            The elevator board is always overflowing — not enough space, too many
            voices trying to be heard. No one fixed it. So I did.
          </p>
          <p className='text-gray-700 text-[15px] font-light leading-loose tracking-wide'>
            Here's an infinite one. Post your collaboration poster in seconds,
            no account needed. It stays up for 3 weeks.
          </p>
          <p className='text-gray-900 text-[15px] font-normal tracking-wide'>Enjoy.</p>
          <p className='text-gray-400 text-sm italic tracking-widest'>— Rain</p>
        </div>

        {/* Preview area */}
        {previewUrl && stage !== 'uploading' && (
          <div className='mb-6 relative'>
            <img
              src={previewUrl}
              alt='Selected poster'
              className='w-full rounded-2xl object-contain max-h-64 bg-gray-50'
            />
            <button
              onClick={handleReset}
              className='absolute top-2 right-2 bg-white bg-opacity-90 rounded-full w-8 h-8 flex items-center justify-center text-gray-600 hover:text-black shadow-sm'
              aria-label='Remove'
            >
              ✕
            </button>
          </div>
        )}

        {/* Upload progress */}
        {stage === 'uploading' && (
          <div className='mb-6'>
            {previewUrl && (
              <img
                src={previewUrl}
                alt='Uploading poster'
                className='w-full rounded-2xl object-contain max-h-64 bg-gray-50 mb-4 opacity-70'
              />
            )}
            <div className='flex items-center gap-3'>
              <div className='flex-1 h-2 bg-gray-100 rounded-full overflow-hidden'>
                <div
                  className='h-full bg-black rounded-full transition-all duration-200'
                  style={{ width: `${uploadProgress}%` }}
                />
              </div>
              <span className='text-xs text-gray-400 w-10 text-right'>
                {uploadProgress}%
              </span>
            </div>
            <p className='text-xs text-gray-400 mt-2 text-center'>Uploading...</p>
          </div>
        )}

        {/* Error message */}
        {stage === 'error' && errorMsg && (
          <div className='mb-5 p-4 bg-red-50 rounded-xl border border-red-100'>
            <p className='text-red-600 text-sm text-center'>{errorMsg}</p>
          </div>
        )}

        {/* Primary button */}
        {stage !== 'uploading' && (
          <button
            onClick={stage === 'preview' ? handleUpload : handleChooseFile}
            className='w-full bg-black text-white py-4 rounded-xl font-semibold text-base border border-transparent hover:bg-gradient-to-r hover:from-blue-500 hover:to-purple-500 hover:border-transparent active:scale-95 transition-all duration-200 mb-3'
          >
            {stage === 'preview' ? 'Post this Poster' : 'Post a Poster'}
          </button>
        )}

        {/* Change image (preview state) */}
        {stage === 'preview' && (
          <button
            onClick={handleChooseFile}
            className='w-full text-gray-400 text-sm py-2 hover:text-gray-600 transition-colors'
          >
            Choose a different image
          </button>
        )}

        {/* Hint */}
        {(stage === 'idle' || stage === 'error') && (
          <p className='text-center text-xs text-gray-400 mt-3'>
            Max 2MB · Stays for 21 days
          </p>
        )}
      </div>
    </div>
  );
};

export default PostPosterPage;
